/* 
 * Component: Collection Grid Page
 * Description: Advanced product grid with filtering, search, and sorting functionality
 */

console.log('📄 Collection Grid Page JS loaded');

// Global collection products data (previously inline script)
var collectionProducts = {
  'all': []
};

class CollectionGridPage {
  constructor(element) {
    console.log('🚀 CollectionGridPage constructor called', element);
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    
    // Cache DOM elements
    this.productCards = element.querySelectorAll('.product-card');
    this.collectionLinks = element.querySelectorAll('.collection-filter-link');
    this.sortSelect = element.querySelector('.sort-by-dropdown select');
    this.searchInput = element.querySelector('.product-search-input');
    this.searchClear = element.querySelector('.search-clear');
    this.sidebar = element.querySelector('.sidebar-filters');
    
    // Search state
    this.searchTimeout = null;
    this.currentActiveCollection = 'all';
    this.element.classList.add('is-initializing');
    
    this.init();
  }

  init() {
    this.initImageSliders();
    this.initColorSwatches();
    this.initCollectionFilters();
    this.initSorting();
    this.initSearch();
    this.initSidebarFilters();
    this.initFullCardClickable();
    
    // Apply initial sort before first paint after init to prevent reorder flash.
    this.applySorting();
    requestAnimationFrame(() => {
      this.element.classList.remove('is-initializing');
    });
  }

  initImageSliders() {
    const sliders = this.element.querySelectorAll('.product-slider');
    
    sliders.forEach(slider => {
      const wrapper = slider.querySelector('.product-slider-wrapper');
      const slides = slider.querySelectorAll('.product-slide');
      const card = slider.closest('.product-card');
      const dots = card.querySelectorAll('.dot');
      
      if (slides.length <= 1) return;
      
      let currentSlide = 0;
      const slideCount = slides.length;
      
      this.updateDots(dots, currentSlide);
      
      // Touch swipe functionality
      let touchStartX = 0;
      let touchEndX = 0;
      
      slider.addEventListener('touchstart', e => {
        touchStartX = e.changedTouches[0].screenX;
      }, {passive: true});
      
      slider.addEventListener('touchend', e => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX, currentSlide, slideCount, wrapper, dots, (newSlide) => {
          currentSlide = newSlide;
        });
      }, {passive: true});
      
      // Mouse drag functionality
      this.initMouseDrag(slider, wrapper, dots, currentSlide, slideCount, (newSlide) => {
        currentSlide = newSlide;
      });
      
      // Connect dots to slides
      if (dots && dots.length) {
        dots.forEach((dot, i) => {
          dot.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            currentSlide = i;
            this.updateSlider(wrapper, currentSlide);
            this.updateDots(dots, currentSlide);
          });
        });
      }
    });
  }

  initMouseDrag(slider, wrapper, dots, currentSlide, slideCount, setCurrentSlide) {
    let isDragging = false;
    let startPosX = 0;
    
    slider.addEventListener('mousedown', e => {
      e.preventDefault();
      isDragging = true;
      startPosX = e.clientX;
      slider.style.cursor = 'grabbing';
    });
    
    slider.addEventListener('mousemove', e => {
      if (!isDragging) return;
      e.preventDefault();
      const moveX = e.clientX - startPosX;
      const slideWidth = slider.offsetWidth;
      
      if (Math.abs(moveX) < slideWidth * 0.5) {
        wrapper.style.transform = `translateX(${-currentSlide * 100 + (moveX / slideWidth * 100)}%)`;
      }
    });
    
    const handleMouseUp = (e) => {
      if (!isDragging) return;
      
      isDragging = false;
      slider.style.cursor = 'grab';
      
      const moveX = e.clientX - startPosX;
      const threshold = slider.offsetWidth * 0.2;
      
      if (moveX < -threshold) {
        currentSlide = Math.min(currentSlide + 1, slideCount - 1);
      } else if (moveX > threshold) {
        currentSlide = Math.max(currentSlide - 1, 0);
      }
      
      setCurrentSlide(currentSlide);
      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    };
    
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        slider.style.cursor = 'grab';
        this.updateSlider(wrapper, currentSlide);
      }
    });
  }

  handleSwipe(startX, endX, currentSlide, slideCount, wrapper, dots, setCurrentSlide) {
    const threshold = 50;
    
    if (startX - endX > threshold) {
      currentSlide = Math.min(currentSlide + 1, slideCount - 1);
      setCurrentSlide(currentSlide);
      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    } else if (endX - startX > threshold) {
      currentSlide = Math.max(currentSlide - 1, 0);
      setCurrentSlide(currentSlide);
      this.updateSlider(wrapper, currentSlide);
      this.updateDots(dots, currentSlide);
    }
  }

  updateSlider(wrapper, currentSlide) {
    wrapper.style.transform = `translateX(-${currentSlide * 100}%)`;
  }

  updateDots(dots, currentSlide) {
    if (dots && dots.length) {
      dots.forEach((dot, i) => {
        if (i === currentSlide) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }
  }

  initColorSwatches() {
    const swatches = this.element.querySelectorAll('.color-swatch');
    
    swatches.forEach(swatch => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        const productCard = swatch.closest('.product-card');
        const variantImage = swatch.getAttribute('data-variant-image');
        const cardSwatches = productCard.querySelectorAll('.color-swatch');
        
        // Remove active class from all swatches in this product
        cardSwatches.forEach(s => s.classList.remove('active'));
        
        // Add active class to clicked swatch
        swatch.classList.add('active');
        
        // Change product image if variant has an image
        if (variantImage && variantImage !== '') {
          const productImages = productCard.querySelectorAll('.product-image');
          if (productImages.length > 0) {
            productImages[0].src = variantImage;
          }
        }
      });
    });
  }

  initCollectionFilters() {
    // Handle initial state based on URL collection or active link
    const activeLink = this.element.querySelector('.collection-filter-link.active');
    console.log('🎯 Active link found:', activeLink);
    console.log('🔗 All collection links:', this.element.querySelectorAll('.collection-filter-link'));
    
    if (activeLink) {
      const activeCollection = activeLink.getAttribute('data-collection');
      this.currentActiveCollection = activeCollection;
      console.log('✅ Setting active collection from link:', activeCollection);
      
      if (activeCollection === 'all') {
        // Show all products when "Shop All" is active
        this.productCards.forEach(card => {
          card.style.display = '';
        });
      } else if (activeCollection !== 'all') {
        // Filter products by specific collection
        this.productCards.forEach(card => {
          const cardCollections = card.getAttribute('data-collection') || '';
          if (cardCollections.includes(activeCollection)) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });
      }
    } else {
      // No active link, try to detect from URL or page context
      console.log('❌ No active link found, trying URL detection');
      
      // Try to get current collection from URL
      const path = window.location.pathname;
      const collectionMatch = path.match(/\/collections\/([^\/\?]+)/);
      
      if (collectionMatch && collectionMatch[1] && collectionMatch[1] !== 'all') {
        this.currentActiveCollection = collectionMatch[1];
        console.log('🌐 Detected collection from URL:', this.currentActiveCollection);
        
        // Filter products by detected collection
        this.productCards.forEach(card => {
          const cardCollections = card.getAttribute('data-collection') || '';
          if (cardCollections.includes(this.currentActiveCollection)) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });
      } else {
        // Fallback to all
        console.log('🔄 Defaulting to all collection');
        this.currentActiveCollection = 'all';
        this.productCards.forEach(card => {
          card.style.display = '';
        });
      }
    }
    
    console.log('Initial active collection set to:', this.currentActiveCollection);
    
    this.collectionLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Remove active class from all links
        this.collectionLinks.forEach(l => l.classList.remove('active'));
        
        // Add active class to clicked link
        link.classList.add('active');
        
        const collectionHandle = link.getAttribute('data-collection');
        this.currentActiveCollection = collectionHandle;
        
        console.log('Collection changed to:', collectionHandle);
        
        // Show/hide product cards based on collection
        this.productCards.forEach(card => {
          const cardCollections = card.getAttribute('data-collection') || '';
          
          let shouldShow = false;
          
          if (collectionHandle === 'all') {
            shouldShow = true;
          } else {
            shouldShow = cardCollections.includes(collectionHandle);
          }
          
          if (shouldShow) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });
        
        // Apply sorting after filtering to maintain correct order
        setTimeout(() => {
          this.applySorting();
        }, 10);

        // Clear search when switching collections
        if (this.searchInput && this.searchInput.value.trim() !== '') {
          this.searchInput.value = '';
          if (this.searchClear) this.searchClear.style.display = 'none';
          this.element.classList.remove('searching');
        }
      });
    });
  }

  initSorting() {
    if (!this.sortSelect) return;
    
    this.sortSelect.addEventListener('change', () => {
      this.applySorting();
    });
  }
  
  applySorting() {
    if (!this.sortSelect) return;
    
    const selectedSort = this.sortSelect.value;
    const productGrid = this.element.querySelector('.product-grid');
    
    // Get only the visible products
    const visibleProducts = Array.from(this.productCards).filter(card => card.style.display !== 'none');
    
    console.log('Applying sort:', selectedSort);
    console.log('Current active collection:', this.currentActiveCollection);
    console.log('Visible products count:', visibleProducts.length);
    
    // Sort products based on selection
    visibleProducts.sort((a, b) => {
      switch(selectedSort) {
        case 'price-ascending':
          return parseFloat(a.getAttribute('data-price')) - parseFloat(b.getAttribute('data-price'));
        case 'price-descending':
          return parseFloat(b.getAttribute('data-price')) - parseFloat(a.getAttribute('data-price'));
        case 'title-ascending':
          return a.getAttribute('data-title').localeCompare(b.getAttribute('data-title'));
        case 'title-descending':
          return b.getAttribute('data-title').localeCompare(a.getAttribute('data-title'));
        case 'created-ascending':
          return new Date(a.getAttribute('data-created')) - new Date(b.getAttribute('data-created'));
        case 'created-descending':
          return new Date(b.getAttribute('data-created')) - new Date(a.getAttribute('data-created'));
        default: // best-selling or default
          // Use collection-specific order when a specific collection is active
          if (this.currentActiveCollection && this.currentActiveCollection !== 'all') {
            const aPositions = this.parseCollectionPositions(a.getAttribute('data-collection-positions'));
            const bPositions = this.parseCollectionPositions(b.getAttribute('data-collection-positions'));
            
            const aPos = aPositions[this.currentActiveCollection] || 999999;
            const bPos = bPositions[this.currentActiveCollection] || 999999;
            
            return aPos - bPos;
          } else {
            // Fallback to original index for 'all' collection  
            return parseInt(a.getAttribute('data-index')) - parseInt(b.getAttribute('data-index'));
          }
      }
    });
    
    // Re-append sorted products to the grid
    visibleProducts.forEach(product => {
      productGrid.appendChild(product);
    });
  }

  parseCollectionPositions(positionsString) {
    const positions = {};
    if (!positionsString) return positions;
    
    const pairs = positionsString.split('|');
    pairs.forEach(pair => {
      const [collection, position] = pair.split(':');
      if (collection && position) {
        positions[collection] = parseInt(position);
      }
    });
    
    return positions;
  }

  initSearch() {
    if (!this.searchInput) return;
    
    this.searchInput.addEventListener('input', (e) => {
      const searchTerm = e.target.value.toLowerCase().trim();
      
      // Show/hide clear button
      if (searchTerm.length > 0) {
        if (this.searchClear) this.searchClear.style.display = 'flex';
        this.element.classList.add('searching');
      } else {
        if (this.searchClear) this.searchClear.style.display = 'none';
        this.element.classList.remove('searching');
      }
      
      // Debounce search for better performance
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.performSearch(searchTerm);
      }, 300);
    });
    
    // Clear search handler
    if (this.searchClear) {
      this.searchClear.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchClear.style.display = 'none';
        this.element.classList.remove('searching');
        this.showAllProducts();
      });
    }
  }

  performSearch(searchTerm) {
    if (searchTerm === '') {
      this.showAllProducts();
      return;
    }
    
    this.productCards.forEach(card => {
      const title = card.getAttribute('data-title')?.toLowerCase() || '';
      const vendor = card.getAttribute('data-vendor')?.toLowerCase() || '';
      const tags = card.getAttribute('data-tags')?.toLowerCase() || '';
      const type = card.getAttribute('data-type')?.toLowerCase() || '';
      
      // Search in title, vendor, tags, and type
      const isMatch = title.includes(searchTerm) || 
                     vendor.includes(searchTerm) || 
                     tags.includes(searchTerm) ||
                     type.includes(searchTerm);
      
      if (isMatch) {
        card.classList.remove('hidden');
        card.style.display = '';
      } else {
        card.classList.add('hidden');
        setTimeout(() => {
          if (card.classList.contains('hidden')) {
            card.style.display = 'none';
          }
        }, 300);
      }
    });
  }

  showAllProducts() {
    this.productCards.forEach(card => {
      card.classList.remove('hidden');
      card.style.display = '';
    });
  }

  initSidebarFilters() {
    if (!this.sidebar) return;
    
    const clearBtn = this.sidebar.querySelector('[data-clear-filters]');
    const inputs = this.sidebar.querySelectorAll('.sf-input');
    const header = this.sidebar.querySelector('.sidebar-filters__header');
    const content = this.sidebar.querySelector('.sidebar-filters__content');
    
    // Mobile dropdown functionality
    const initMobileDropdown = () => {
      if (window.innerWidth <= 767) {
        this.sidebar.classList.add('collapsed');
      } else {
        this.sidebar.classList.remove('collapsed');
      }
    };
    
    // Toggle dropdown on header click (mobile only)
    if (header) {
      header.addEventListener('click', (e) => {
        if (window.innerWidth <= 767) {
          if (!e.target.matches('.sidebar-filters__clear') && !e.target.closest('.sidebar-filters__clear')) {
            e.preventDefault();
            this.sidebar.classList.toggle('collapsed');
          }
        }
      });
    }
    
    // Initialize and handle resize
    initMobileDropdown();
    window.addEventListener('resize', initMobileDropdown);
    
    // Filter functionality
    const getActiveFilters = () => {
      const groups = {};
      inputs.forEach(input => {
        if (input.checked) {
          (groups[input.dataset.filterGroup] ||= []).push(input.value);
        }
      });
      return groups;
    };
    
    const matchesFilters = (card, groups) => {
      const vendor = this.normalize(card.dataset.vendor);
      const tags = (card.dataset.tags || '').toLowerCase();
      const title = this.normalize(card.dataset.title);
      const type = this.normalize(card.dataset.type);
      const cardCollections = card.getAttribute('data-collection') || '';

      for (const [group, values] of Object.entries(groups)) {
        if (!values.length) continue;
        const any = values.some(v => {
          const val = v.toLowerCase();
          
          if (group === 'collection') {
            if (val === 'all') {
              return true;
            } else {
              const collections = cardCollections.split(' ').filter(c => c.length > 0);
              return collections.includes(val);
            }
          } else if (group === 'size') {
            return title.includes(val) || tags.includes(val);
          } else if (group === 'color') {
            return title.includes(val) || tags.includes(val);
          } else if (group === 'material') {
            return title.includes(val) || tags.includes(val) || type.includes(val);
          } else if (group === 'style') {
            return title.includes(val) || tags.includes(val) || type.includes(val);
          } else {
            return vendor.includes(val) || tags.includes(val) || title.includes(val) || type.includes(val);
          }
        });
        if (!any) return false;
      }
      return true;
    };
    
    const applyFilters = () => {
      const groups = getActiveFilters();
      const hasFilters = Object.values(groups).some(arr => arr && arr.length);
      
      this.productCards.forEach(card => {
        if (!hasFilters || matchesFilters(card, groups)) {
          card.style.display = '';
        } else {
          card.style.display = 'none';
        }
      });
    };
    
    // Apply filters on button click
    const applyBtn = this.element.querySelector('[data-apply-filters]');
    if (applyBtn) {
      applyBtn.addEventListener('click', applyFilters);
    }
    
    // Clear filters
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        inputs.forEach(input => input.checked = false);
        applyFilters();
      });
    }
    
    // Initial filter application (run immediately to avoid post-render jumping)
    applyFilters();
  }

  initFullCardClickable() {
    const clickableCards = this.element.querySelectorAll('.product-card.full-card-clickable');
    
    clickableCards.forEach(card => {
      const productUrl = card.getAttribute('data-product-url');
      if (!productUrl) return;
      
      let isSliding = false;
      let startX = 0;
      let startY = 0;
      const slideThreshold = 10;
      
      const handleStart = (e) => {
        isSliding = false;
        const touch = e.touches ? e.touches[0] : e;
        startX = touch.clientX;
        startY = touch.clientY;
      };
      
      const handleMove = (e) => {
        if (!startX || !startY) return;
        
        const touch = e.touches ? e.touches[0] : e;
        const deltaX = Math.abs(touch.clientX - startX);
        const deltaY = Math.abs(touch.clientY - startY);
        
        if (deltaX > slideThreshold || deltaY > slideThreshold) {
          isSliding = true;
        }
      };
      
      const handleCardClick = (e) => {
        if (isSliding) return;
        
        const clickedElement = e.target;
        if (clickedElement.closest('a, button, input, select, textarea')) {
          return;
        }
        
        if (clickedElement.closest('.product-dots, .dot')) {
          return;
        }
        
        window.location.href = productUrl;
      };
      
      // Add event listeners
      card.addEventListener('mousedown', handleStart);
      card.addEventListener('mousemove', handleMove);
      card.addEventListener('click', handleCardClick);
      
      card.addEventListener('touchstart', handleStart, { passive: true });
      card.addEventListener('touchmove', handleMove, { passive: true });
      
      card.addEventListener('touchend', () => {
        setTimeout(() => { isSliding = false; }, 100);
      }, { passive: true });
      
      card.addEventListener('mouseup', () => {
        setTimeout(() => { isSliding = false; }, 100);
      });
    });
  }

  normalize(str) {
    return (str || '').toString().toLowerCase().replace(/\s+/g, ' ').trim();
  }

  destroy() {
    // Clean up event listeners if needed
    clearTimeout(this.searchTimeout);
  }
}

// Auto-initialize components
document.addEventListener('DOMContentLoaded', () => {
  console.log('📋 DOMContentLoaded fired for collection grid');
  const elements = document.querySelectorAll('[data-component="collection-grid-page"]');
  console.log('🔍 Found', elements.length, 'collection grid elements');
  elements.forEach(element => {
    new CollectionGridPage(element);
  });
});

// Handle section reloads in theme editor
document.addEventListener('shopify:section:load', (event) => {
  const components = event.target.querySelectorAll('[data-component="collection-grid-page"]');
  components.forEach(element => {
    new CollectionGridPage(element);
  });
});

//update cart on qty action
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', e => {
    const plus = e.target.closest('.ey-qty-plus');
    const minus = e.target.closest('.ey-qty-minus');
    if (!plus && !minus) return;

    const wrapper = e.target.closest('.ey-qty-cart');
    if (!wrapper) return;

    const input = wrapper.querySelector('.ey-qty-input');
    const variantId = wrapper.dataset.productId;
    if (!variantId || !input) return;

    if (wrapper.classList.contains('ey-qty-cart--loading')) return;

    const minusBtn = wrapper.querySelector('.ey-qty-minus');
    const plusBtn = wrapper.querySelector('.ey-qty-plus');

    function setLoading(loading) {
      wrapper.classList.toggle('ey-qty-cart--loading', loading);
      if (minusBtn) minusBtn.disabled = loading;
      if (plusBtn) plusBtn.disabled = loading;
      if (input) {
        input.readOnly = loading;
        input.disabled = loading;
      }
    }

    let qty = parseInt(input.value, 10) || 0;

    // ✅ Update cart bubble manually
    function updateCartBubble(cart) {
      const bubble = document.querySelector('.cart-count-bubble');
      if (!bubble) return;

      const visibleCount = bubble.querySelector('span[aria-hidden="true"]');
      const hiddenCount = bubble.querySelector('.visually-hidden');

      if (visibleCount) {
        visibleCount.textContent = cart.item_count;
      }
      if (hiddenCount) {
        hiddenCount.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
      }

      // 🔑 Force bubble visible when items > 0
      bubble.style.display = cart.item_count > 0 ? 'inline-block' : 'none';
    }

    // ✅ Refresh cart drawer silently
    function refreshCartDrawer(parsedState) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(parsedState, { open: false });
      }

      // 🔑 After drawer refresh, fix bubble visibility
      if (parsedState && parsedState.item_count !== undefined) {
        updateCartBubble(parsedState);
      }
    }

    // Only request drawer section
    const sections = ['cart-drawer'];

    if (plus) {
      setLoading(true);
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          sections,
          sections_url: window.location.pathname
        })
      })
        .then(res => res.json())
        .then(parsedState => {
          // Update the current input immediately
          input.value = qty + 1;
          
          // Refresh cart drawer and sync all quantity inputs
          refreshCartDrawer(parsedState);
          
          // Trigger cart update event for quantity sync
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, parsedState);
          }
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          // Reset the input value on error
          input.value = qty;
        })
        .finally(() => setLoading(false));
    }

    if (minus && qty > 0) {
      setLoading(true);
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: qty - 1,
          sections,
          sections_url: window.location.pathname
        })
      })
        .then(res => res.json())
        .then(parsedState => {
          // Update the current input immediately  
          input.value = qty - 1;
          
          // Refresh cart drawer and sync all quantity inputs
          refreshCartDrawer(parsedState);
          
          // Trigger cart update event for quantity sync
          if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, parsedState);
          }
        })
        .catch(error => {
          console.error('Error updating cart:', error);
          // Reset the input value on error
          input.value = qty;
        })
        .finally(() => setLoading(false));
    }
  });
});

// Reset all quantity inputs when cart is cleared
document.addEventListener('DOMContentLoaded', () => {
  // Function to reset all product quantity inputs to 0
  function resetAllQuantityInputs() {
    const allQuantityInputs = document.querySelectorAll('.ey-qty-input');
    allQuantityInputs.forEach(input => {
      input.value = 0;
    });
  }

  // Function to sync quantity inputs with actual cart state
  function syncQuantityInputsWithCart() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const allQuantityInputs = document.querySelectorAll('.ey-qty-input');
        
        if (cart.item_count === 0) {
          // Cart is empty, reset all quantity inputs to 0
          allQuantityInputs.forEach(input => {
            input.value = 0;
          });
        } else {
          // Cart has items, sync quantities
          allQuantityInputs.forEach(input => {
            const variantId = input.closest('.ey-qty-cart').dataset.productId;
            const cartItem = cart.items.find(item => item.variant_id.toString() === variantId);
            input.value = cartItem ? cartItem.quantity : 0;
          });
        }
      })
      .catch(error => {
        console.error('Error fetching cart data:', error);
        // If fetch fails, reset to 0 to be safe
        resetAllQuantityInputs();
      });
  }

  // Sync on page load
  syncQuantityInputsWithCart();

  // Subscribe to cart updates using the theme's pub/sub system
  if (window.subscribe && window.PUB_SUB_EVENTS) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      syncQuantityInputsWithCart();
    });
  }

  // Listen for direct cart clear events
  document.addEventListener('cartCleared', () => {
    resetAllQuantityInputs();
  });

  // Watch for cart drawer/modal closes with empty cart
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (!cartDrawer.classList.contains('active')) {
            // Cart drawer closed, sync quantities
            setTimeout(syncQuantityInputsWithCart, 100);
          }
        }
      });
    });
    
    observer.observe(cartDrawer, { attributes: true });
  }

  // Periodic sync every 5 seconds when page is visible (fallback)
  let syncInterval;
  function startPeriodicSync() {
    if (syncInterval) clearInterval(syncInterval);
    syncInterval = setInterval(() => {
      if (!document.hidden) {
        syncQuantityInputsWithCart();
      }
    }, 5000);
  }

  function stopPeriodicSync() {
    if (syncInterval) {
      clearInterval(syncInterval);
      syncInterval = null;
    }
  }

  // Start periodic sync
  startPeriodicSync();

  // Handle page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      stopPeriodicSync();
    } else {
      syncQuantityInputsWithCart();
      startPeriodicSync();
    }
  });
});



